import React, { useState, useEffect } from 'react';
import StudentNavigation from '../../components/ui/StudentNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { groupCounselingService } from '../../services/groupCounselingService';
import JitsiMeetRoom from '../../components/JitsiMeetRoom';

const GroupCounselingSessions = () => {
  const { user, userProfile } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [myRegistrations, setMyRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [registering, setRegistering] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.id) loadData();
  }, [user?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [allSessions, myRegs] = await Promise.all([
        groupCounselingService?.getUpcomingSessions(),
        groupCounselingService?.getStudentRegistrations(user?.id)
      ]);
      setSessions(allSessions || []);
      setMyRegistrations(myRegs || []);
    } catch (err) {
      console.error('Error loading group sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (sessionId) => {
    try {
      setRegistering(sessionId);
      setError(null);
      await groupCounselingService?.registerStudent(sessionId, user?.id);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to register. Please try again.');
    } finally {
      setRegistering(null);
    }
  };

  const handleCancelRegistration = async (sessionId) => {
    try {
      setRegistering(sessionId);
      await groupCounselingService?.cancelRegistration(sessionId, user?.id);
      await loadData();
    } catch (err) {
      console.error('Error cancelling registration:', err);
    } finally {
      setRegistering(null);
    }
  };

  const handleJoin = (session) => {
    setActiveSession(session);
  };

  const isRegistered = (sessionId) =>
    myRegistrations?.some(r => r?.session_id === sessionId);

  const getRegisteredSessionIds = () =>
    new Set(myRegistrations?.map(r => r?.session_id));

  const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d?.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  const getStatusBadge = (status) => {
    const map = {
      scheduled: 'bg-blue-100 text-blue-700',
      ongoing: 'bg-green-100 text-green-700',
      completed: 'bg-gray-100 text-gray-600',
      cancelled: 'bg-red-100 text-red-600'
    };
    return map?.[status] || 'bg-muted text-muted-foreground';
  };

  if (activeSession) {
    const joinCheck = groupCounselingService?.canStudentJoin(activeSession);
    if (!joinCheck?.canJoin) {
      return (
        <div className="min-h-screen bg-background">
          <StudentNavigation />
          <main className="pt-[60px]">
            <div className="max-w-2xl mx-auto px-4 py-16 text-center">
              <Icon name="Lock" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">Cannot Join Session</h2>
              <p className="text-muted-foreground mb-6">{joinCheck?.reason}</p>
              <Button onClick={() => setActiveSession(null)}>Back to Sessions</Button>
            </div>
          </main>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-background">
        <StudentNavigation />
        <main className="pt-[60px]">
          <div className="max-w-[1440px] mx-auto px-4 py-6">
            <div className="flex items-center gap-3 mb-4">
              <Button variant="outline" size="sm" iconName="ArrowLeft" iconPosition="left"
                onClick={() => setActiveSession(null)}>
                Back
              </Button>
              <div>
                <h2 className="text-lg font-semibold text-foreground">{activeSession?.title}</h2>
                <p className="text-sm text-muted-foreground">
                  {activeSession?.institute?.institute_profiles?.[0]?.institute_name ||
                    activeSession?.institute?.name}
                </p>
              </div>
            </div>
            <JitsiMeetRoom
              roomName={activeSession?.jitsi_room_name}
              displayName={userProfile?.full_name || user?.name || 'Student'}
              title={activeSession?.title}
              onClose={() => setActiveSession(null)}
              onLeave={() => setActiveSession(null)}
            />
          </div>
        </main>
      </div>
    );
  }

  const registeredIds = getRegisteredSessionIds();
  const mySessions = myRegistrations?.map(r => r?.session)?.filter(Boolean);

  return (
    <div className="min-h-screen bg-background">
      <StudentNavigation />
      <main className="pt-[60px]">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
              Group Counseling Sessions
            </h1>
            <p className="text-muted-foreground">
              Join live group sessions with institute counselors — ask questions, get answers, connect with peers.
            </p>
          </div>

          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-center gap-2 mb-4">
              <Icon name="AlertCircle" size={16} color="var(--color-error)" />
              <span className="text-sm text-error">{error}</span>
              <button onClick={() => setError(null)} className="ml-auto"><Icon name="X" size={14} /></button>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 bg-muted rounded-lg p-1 mb-6 w-fit">
            {[
              { id: 'upcoming', label: 'Upcoming Sessions', icon: 'Calendar' },
              { id: 'my', label: `My Registrations (${myRegistrations?.length})`, icon: 'CheckCircle' }
            ]?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab?.id
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="Loader2" size={32} className="animate-spin text-muted-foreground" />
            </div>
          ) : activeTab === 'upcoming' ? (
            sessions?.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Icon name="Users" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No Sessions Available</h3>
                <p className="text-muted-foreground">Check back soon — institutes will post group counseling sessions here.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {sessions?.map(session => {
                  const registered = registeredIds?.has(session?.id);
                  const joinCheck = groupCounselingService?.canStudentJoin(session);
                  const regCount = session?.registrations?.filter(r => r?.status === 'registered')?.length || 0;
                  const isFull = regCount >= session?.max_participants;
                  const instituteName =
                    session?.institute?.institute_profiles?.[0]?.institute_name ||
                    session?.institute?.name || 'Institute';

                  return (
                    <div key={session?.id} className="bg-card border border-border rounded-xl p-5 flex flex-col gap-4">
                      {/* Institute + Status */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Icon name="Building2" size={18} color="var(--color-primary)" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">by</p>
                            <p className="text-sm font-medium text-foreground leading-tight">{instituteName}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full flex-shrink-0 ${getStatusBadge(session?.status)}`}>
                          {session?.status === 'ongoing' ? '🔴 Live' : session?.status?.charAt(0)?.toUpperCase() + session?.status?.slice(1)}
                        </span>
                      </div>

                      {/* Title + Topic */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">{session?.title}</h3>
                        {session?.topic && (
                          <span className="inline-block bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full">
                            {session?.topic}
                          </span>
                        )}
                        {session?.description && (
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{session?.description}</p>
                        )}
                      </div>

                      {/* Details */}
                      <div className="space-y-1.5 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Icon name="Calendar" size={14} />
                          <span>{formatDateTime(session?.scheduled_at)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="Clock" size={14} />
                          <span>{session?.duration_minutes} minutes</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="Users" size={14} />
                          <span>{regCount} / {session?.max_participants} registered</span>
                          {isFull && <span className="text-xs text-error font-medium">(Full)</span>}
                        </div>
                        {session?.allow_late_entry && (
                          <div className="flex items-center gap-2 text-green-600">
                            <Icon name="Clock" size={14} />
                            <span className="text-xs">Late entry allowed (+{session?.late_entry_minutes} min)</span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="mt-auto pt-2 border-t border-border">
                        {registered ? (
                          <div className="space-y-2">
                            {(session?.status === 'ongoing' || session?.status === 'scheduled') && joinCheck?.canJoin && (
                              <Button className="w-full bg-green-600 hover:bg-green-700"
                                iconName="Video" iconPosition="left"
                                onClick={() => handleJoin(session)}>
                                {session?.status === 'ongoing' ? 'Join Live Session' : 'Enter Session Room'}
                              </Button>
                            )}
                            {!joinCheck?.canJoin && (
                              <p className="text-xs text-center text-muted-foreground">{joinCheck?.reason}</p>
                            )}
                            <Button variant="outline" size="sm" className="w-full text-error border-error/30 hover:bg-error/5"
                              onClick={() => handleCancelRegistration(session?.id)}
                              disabled={registering === session?.id}>
                              {registering === session?.id ? 'Cancelling...' : 'Cancel Registration'}
                            </Button>
                          </div>
                        ) : (
                          <Button
                            className="w-full"
                            onClick={() => handleRegister(session?.id)}
                            disabled={registering === session?.id || isFull || session?.status === 'completed' || session?.status === 'cancelled'}
                          >
                            {registering === session?.id ? 'Registering...' : isFull ?'Session Full' :
                              session?.status === 'completed' ? 'Session Ended' :
                              session?.status === 'cancelled'? 'Cancelled' : 'Register Now'}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            // My Registrations Tab
            (mySessions?.length === 0 ? (<div className="bg-card border border-border rounded-lg p-12 text-center">
              <Icon name="CheckCircle" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No Registrations Yet</h3>
              <p className="text-muted-foreground mb-4">Browse upcoming sessions and register to attend.</p>
              <Button onClick={() => setActiveTab('upcoming')}>Browse Sessions</Button>
            </div>) : (<div className="space-y-4">
              {mySessions?.map(session => {
                if (!session) return null;
                const joinCheck = groupCounselingService?.canStudentJoin(session);
                const instituteName =
                  session?.institute?.institute_profiles?.[0]?.institute_name ||
                  session?.institute?.name || 'Institute';

                return (
                  <div key={session?.id} className="bg-card border border-border rounded-xl p-5">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-semibold text-foreground">{session?.title}</h3>
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusBadge(session?.status)}`}>
                            {session?.status === 'ongoing' ? '🔴 Live' : session?.status?.charAt(0)?.toUpperCase() + session?.status?.slice(1)}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">🏛️ {instituteName}</p>
                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Icon name="Calendar" size={14} />
                            {formatDateTime(session?.scheduled_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="Clock" size={14} />
                            {session?.duration_minutes} min
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {joinCheck?.canJoin ? (
                          <Button iconName="Video" iconPosition="left"
                            className={session?.status === 'ongoing' ? 'bg-green-600 hover:bg-green-700' : ''}
                            onClick={() => handleJoin(session)}>
                            {session?.status === 'ongoing' ? 'Join Live' : 'Enter Room'}
                          </Button>
                        ) : (
                          <span className="text-sm text-muted-foreground self-center">{joinCheck?.reason}</span>
                        )}
                        <Button variant="outline" size="sm"
                          onClick={() => handleCancelRegistration(session?.id)}
                          disabled={registering === session?.id || session?.status === 'completed'}>
                          <Icon name="X" size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>))
          )}
        </div>
      </main>
    </div>
  );
};

export default GroupCounselingSessions;
